import React, { useMemo } from 'react';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Box, Flex } from '@rover/kibble/core';
import type { FrontendConfigurationSettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/frontendConfigurationSettingsDogSizeBuckets';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { Pet } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type {
  ServiceType as ServiceTypeType,
  ServiceTypeOption,
  ServiceTypeSlug,
} from '@rover/types/src/ServiceType';

import { DatesSection, LocationSection, PetsSection, ServiceTypeSection } from './components';

type Props = {
  searchFilters: SearchFilters;
  serviceType: ServiceTypeOption | null;
  serviceTypeOptions: ServiceTypeOption[];
  locale: Locale;
  onServiceTypeChange: (filters: { serviceType: ServiceTypeSlug }) => void;
  updateFiltersAndFireSearchOnChange: (filters: Partial<SearchFilters>) => void;
  allPets: Pet[];
  petSizeBuckets: FrontendConfigurationSettingsDogSizeBuckets;
  weightUnit: string;
  allServiceTypeFilterOptions: Record<string, SearchFiltersOptions>;
  getServiceTypeBySlug: (arg0: string) => ServiceTypeType | null | undefined;
};

const Separator = (): JSX.Element => (
  <Box
    width="2px"
    height="100%"
    sx={(theme) => ({ backgroundColor: theme.colors.border.primary })}
  />
);

const SimplifiedFiltersBar = ({
  searchFilters,
  serviceType,
  serviceTypeOptions,
  locale,
  onServiceTypeChange,
  updateFiltersAndFireSearchOnChange,
  allPets,
  petSizeBuckets,
  weightUnit,
  allServiceTypeFilterOptions,
  getServiceTypeBySlug,
}: Props): JSX.Element => {
  const { location, dogCount, catCount, puppyCount, pet: selectedPetIds } = searchFilters;

  const [isDropdownOpen, setIsDropdownOpen] = React.useState<
    'serviceType' | 'dates' | 'pets' | null
  >(null);

  const selectedPets = useMemo(() => {
    if (selectedPetIds.length === 0) return [];
    return allPets.filter((pet) => selectedPetIds.includes(pet.pk.toString()));
  }, [allPets, selectedPetIds]);

  const serviceTypeFilterOptions = allServiceTypeFilterOptions[searchFilters.serviceType] || {};

  return (
    <Flex
      width="100%"
      height="60px"
      borderRadius="8px"
      background="primary"
      border="2px solid"
      borderColor="border.primary"
      flexDirection="row"
    >
      <ServiceTypeSection
        serviceType={serviceType}
        serviceTypeOptions={serviceTypeOptions}
        onServiceTypeChange={onServiceTypeChange}
        isDropdownOpen={isDropdownOpen === 'serviceType'}
        setIsDropdownOpen={(value: boolean) =>
          value ? setIsDropdownOpen('serviceType') : setIsDropdownOpen(null)
        }
      />
      <Separator />
      <LocationSection
        location={location}
        updateFiltersAndFireSearchOnChange={updateFiltersAndFireSearchOnChange}
      />
      <Separator />
      <DatesSection
        searchFilters={searchFilters}
        locale={locale}
        updateFiltersAndFireSearchOnChange={updateFiltersAndFireSearchOnChange}
        serviceType={searchFilters.serviceType as ServiceType}
        serviceTypeFilterOptions={serviceTypeFilterOptions}
        isDropdownOpen={isDropdownOpen === 'dates'}
        setIsDropdownOpen={(value: boolean) =>
          value ? setIsDropdownOpen('dates') : setIsDropdownOpen(null)
        }
      />
      <Separator />
      <PetsSection
        dogCount={dogCount}
        catCount={catCount}
        puppyCount={puppyCount}
        selectedPets={selectedPets}
        searchFilters={searchFilters}
        allPets={allPets}
        petSizeBuckets={petSizeBuckets}
        weightUnit={weightUnit}
        updateFiltersAndFireSearchOnChange={updateFiltersAndFireSearchOnChange}
        serviceTypeFilterOptions={serviceTypeFilterOptions}
        getServiceTypeBySlug={getServiceTypeBySlug}
        isDropdownOpen={isDropdownOpen === 'pets'}
        setIsDropdownOpen={(value: boolean) =>
          value ? setIsDropdownOpen('pets') : setIsDropdownOpen(null)
        }
      />
    </Flex>
  );
};

export default SimplifiedFiltersBar;
